import { useMemo } from 'react';

import { ApiEndpoint } from '@/api/registry';
import { useApiQuery } from '@/api/useApiQuery';
import { useEvaluationQuery, type EvaluationRow } from '@/features/forecasts/hooks/useForecastEvaluation';
import { useTenant } from '@/providers/TenantProvider';

// Aggregate per-SKU WAPE comparison for the 2025-05-04 ("5-04") monthly forecast.
//
// Flow (three MDS calls, independent of SKU count):
//   1. evaluation/list on the ETS run for the lt4 WAPE metric → the set of
//      primary keys that have a value for that metric (the aggregate universe).
//   2. evaluation on the ETS run (WAPE lt4) OR'ing all those PKs.
//   3. evaluation on the BFG run (WAPE lt28) OR'ing the same PKs.
// Then join per series and compute diff = |ETS WAPE| − |BFG WAPE|
// (positive = BFG closer to actuals = BFG better).

const EVAL_FILE_ID = 'evaluation_metrics.parquet';

// 5-04 monthly eval runs (from the run manifests). ETS is weekly-grained so its
// 1-month window is lead_lt4; BFG is daily-grained so its 1-month window is
// lead_lt28.
// TODO(engineer): hardcoded eval run UUIDs, pinned to the 5-04 monthly window because
// eval runs aren't resolved dynamically like forecast runs are. Follow-up: extend the
// solutions-service resolution used for forecast runs to also resolve eval run ids by
// fct + model, then drop these constants.
const ETS_EVAL_RUN_ID = 'c192c758-63df-44a6-98bf-7a9ee95a4540';
const BFG_EVAL_RUN_ID = '9bb53cfb-0fcc-4351-acdd-f37a0805c15a';

const ETS_WAPE_METRIC = 'WAPE_per_series_lead_lt4';
const BFG_WAPE_METRIC = 'WAPE_per_series_lead_lt28';

type EvaluationListResponse = { primary_keys: Array<Record<string, string>> };

export type SkuWapeDiff = {
  // The series' primary keys (e.g. { product_id, ship_to_cust_id, ship_to_region }).
  primaryKeys: Record<string, string>;
  // Stable join/label key built from the PK values.
  key: string;
  etsWape: number | null;
  bfgWape: number | null;
  // |ETS| − |BFG| (Incumbent − Foundation Forecasting).
  // Positive = BFG has lower WAPE = BFG reduces error. Null unless both present.
  diff: number | null;
};

export type AggregateSkuWape = {
  rows: SkuWapeDiff[];
  isLoading: boolean;
  isError: boolean;
};

// Deterministic key from a PK record (sorted entries) so ETS and BFG rows for
// the same series line up regardless of key order.
function pkKey(pk: Record<string, unknown>): string {
  return Object.keys(pk)
    .sort()
    .map((k) => `${k}=${String(pk[k])}`)
    .join('|');
}

function indexByPk(results: EvaluationRow[] | undefined): Map<string, number | null> {
  const map = new Map<string, number | null>();
  for (const row of results ?? []) {
    const key = pkKey(row.primary_keys ?? {});
    map.set(key, typeof row.value === 'number' ? row.value : null);
  }
  return map;
}

export function useAggregateSkuWape(): AggregateSkuWape {
  const { tenantAlias } = useTenant();

  // 1. The universe of series that have an ETS lt4 WAPE for this run.
  const listResult = useApiQuery<EvaluationListResponse>(tenantAlias, ApiEndpoint.ModelOutputsEvaluationList, {
    body: {
      run_id: ETS_EVAL_RUN_ID,
      group_id: 'default',
      file_id: EVAL_FILE_ID,
      metric_name: ETS_WAPE_METRIC,
    },
  });

  // The full primary-key universe for this metric/run; every series is OR'd into
  // the evaluation requests below. Memoized so the downstream useMemo deps don't
  // churn each render.
  const primaryKeys = useMemo(() => listResult.data?.primary_keys ?? [], [listResult.data]);
  const hasKeys = primaryKeys.length > 0;

  // 2 & 3. WAPE per series for each model, OR'ing the full PK list in one call.
  const ets = useEvaluationQuery({
    runId: ETS_EVAL_RUN_ID,
    metricName: ETS_WAPE_METRIC,
    primaryKeys,
    enabled: hasKeys,
    batchSize: 0,
  });
  const bfg = useEvaluationQuery({
    runId: BFG_EVAL_RUN_ID,
    metricName: BFG_WAPE_METRIC,
    primaryKeys,
    enabled: hasKeys,
    batchSize: 0,
  });

  const rows = useMemo(() => {
    const etsByPk = indexByPk(ets.data?.results);
    const bfgByPk = indexByPk(bfg.data?.results);

    const out: SkuWapeDiff[] = primaryKeys.map((pk) => {
      const key = pkKey(pk);
      const etsWape = etsByPk.get(key) ?? null;
      const bfgWape = bfgByPk.get(key) ?? null;
      // |ETS| − |BFG|: positive = BFG reduces error vs the incumbent.
      const diff = etsWape != null && bfgWape != null ? Math.abs(etsWape) - Math.abs(bfgWape) : null;
      return { primaryKeys: pk, key, etsWape, bfgWape, diff };
    });

    // Sorted descending by diff (biggest BFG improvement first); series missing a
    // value sink to the bottom.
    out.sort((a, b) => (b.diff ?? -Infinity) - (a.diff ?? -Infinity));
    return out;
  }, [primaryKeys, ets.data, bfg.data]);

  return {
    rows,
    isLoading: listResult.isLoading || (hasKeys && (ets.isLoading || bfg.isLoading)),
    isError: listResult.isError || ets.isError || bfg.isError,
  };
}
